/**
 * Dashboard-only component categories for Version Settings.
 * Stored in selectedVersion.dashboardMeta — does not affect engine/preview.
 *
 * dashboardMeta.componentCategories — { [componentId]: "Folder" | "A:B:C" }
 * dashboardMeta.componentCategoryFolders — top-level folder names (incl. empty folders), ordered
 * dashboardMeta.componentCategoryChildOrder — { [parentPath]: string[] } nested sibling order
 * dashboardMeta.rootLayoutOrder — mixed top-level list: "c:<componentId>" | "f:<folderName>"
 */

/** @param {string} name */
export function normalizeCategoryName(name) {
  if (typeof name !== "string") return "";
  return name.trim();
}

/** @param {string} categoryPath */
export function parseCategoryPath(categoryPath) {
  if (!categoryPath) return [];
  return categoryPath.split(":").map((s) => s.trim()).filter(Boolean);
}

/**
 * v1 create-dialog validation — single segment, no colons.
 * @param {string} name
 * @param {Set<string>} existingNames
 * @returns {string | null} error message or null if valid
 */
export function validateNewCategoryName(name, existingNames) {
  const trimmed = normalizeCategoryName(name);
  if (!trimmed) return "Category name is required.";
  if (trimmed.includes(":")) return "Use a single folder name (no colons).";
  if (existingNames.has(trimmed)) return "A category with this name already exists.";
  return null;
}

/**
 * @param {string} name
 * @param {Set<string>} existingNames
 * @param {string} currentName
 * @returns {string | null}
 */
export function validateRenameCategoryName(name, existingNames, currentName) {
  const trimmed = normalizeCategoryName(name);
  if (!trimmed) return "Category name is required.";
  if (trimmed.includes(":")) return "Use a single folder name (no colons).";
  if (trimmed !== currentName && existingNames.has(trimmed)) {
    return "A category with this name already exists.";
  }
  return null;
}

/** @param {string} path @param {string} oldPath @param {string} newPath */
export function remapCategoryPath(path, oldPath, newPath) {
  if (!path) return path;
  if (path === oldPath) return newPath;
  if (path.startsWith(`${oldPath}:`)) return newPath + path.slice(oldPath.length);
  return path;
}

/**
 * @param {Record<string, string>} categoriesMap
 * @param {string} categoryPath
 * @returns {number}
 */
export function countComponentsInCategory(categoriesMap, categoryPath) {
  if (!categoryPath) return 0;
  return Object.values(categoriesMap || {}).filter(
    (path) => path === categoryPath || path.startsWith(`${categoryPath}:`)
  ).length;
}

/**
 * @param {string} categoryPath e.g. "UI" or "UI:Buttons"
 * @param {string} newSegmentName last segment replacement
 * @returns {string}
 */
export function buildRenamedCategoryPath(categoryPath, newSegmentName) {
  const segments = parseCategoryPath(categoryPath);
  if (segments.length === 0) return normalizeCategoryName(newSegmentName);
  const parent = segments.slice(0, -1);
  const next = normalizeCategoryName(newSegmentName);
  return parent.length > 0 ? `${parent.join(":")}:${next}` : next;
}

/** @param {Record<string, string[]> | null | undefined} childOrderMap */
function remapCategoryChildOrder(childOrderMap, oldPath, newPath) {
  const next = {};
  Object.entries(childOrderMap || {}).forEach(([parentPath, names]) => {
    const remapped = remapCategoryPath(parentPath, oldPath, newPath);
    next[remapped] = names;
  });
  return next;
}

/** @param {Record<string, string[]> | null | undefined} childOrderMap @param {string} categoryPath */
function pruneCategoryChildOrder(childOrderMap, categoryPath) {
  const next = {};
  Object.entries(childOrderMap || {}).forEach(([parentPath, names]) => {
    if (parentPath === categoryPath || parentPath.startsWith(`${categoryPath}:`)) return;
    next[parentPath] = names;
  });
  return next;
}

/**
 * @param {Record<string, string> | null | undefined} categoriesMap
 * @param {string[] | null | undefined} folderNames
 * @param {Record<string, string[]> | null | undefined} orderMap
 * @param {Record<string, string[]> | null | undefined} childOrderMap
 * @param {string} oldPath
 * @param {string} newPath
 */
export function renameCategoryInMeta(categoriesMap, folderNames, orderMap, childOrderMap, oldPath, newPath) {
  const nextCategories = {};
  Object.entries(categoriesMap || {}).forEach(([id, path]) => {
    nextCategories[id] = remapCategoryPath(path, oldPath, newPath);
  });

  const oldTop = parseCategoryPath(oldPath)[0];
  const newTop = parseCategoryPath(newPath)[0];
  let nextFolders = [...(folderNames || [])];
  if (oldTop && newTop && oldTop !== newTop) {
    const idx = nextFolders.indexOf(oldTop);
    if (idx !== -1) nextFolders[idx] = newTop;
    else if (!nextFolders.includes(newTop)) nextFolders.push(newTop);
    nextFolders = nextFolders.filter((name, i, arr) => arr.indexOf(name) === i);
  }

  const nextOrder = {};
  Object.entries(orderMap || {}).forEach(([key, ids]) => {
    if (!key.startsWith("category:")) {
      nextOrder[key] = ids;
      return;
    }
    const orderPath = key.slice("category:".length);
    const remappedPath = remapCategoryPath(orderPath, oldPath, newPath);
    const nextKey = remappedPath === orderPath ? key : `category:${remappedPath}`;
    nextOrder[nextKey] = ids;
  });

  return {
    categories: nextCategories,
    folders: nextFolders,
    order: nextOrder,
    childOrder: remapCategoryChildOrder(childOrderMap, oldPath, newPath),
  };
}

/**
 * @param {Record<string, string> | null | undefined} categoriesMap
 * @param {string[] | null | undefined} folderNames
 * @param {Record<string, string[]> | null | undefined} orderMap
 * @param {Record<string, string[]> | null | undefined} childOrderMap
 * @param {string} categoryPath
 */
export function deleteCategoryFromMeta(categoriesMap, folderNames, orderMap, childOrderMap, categoryPath) {
  const nextCategories = {};
  Object.entries(categoriesMap || {}).forEach(([id, path]) => {
    if (path === categoryPath || path.startsWith(`${categoryPath}:`)) return;
    nextCategories[id] = path;
  });

  const segments = parseCategoryPath(categoryPath);
  let nextFolders = [...(folderNames || [])];
  if (segments.length === 1) {
    nextFolders = nextFolders.filter((name) => name !== segments[0]);
  }

  const nextOrder = {};
  Object.entries(orderMap || {}).forEach(([key, ids]) => {
    if (!key.startsWith("category:")) {
      nextOrder[key] = ids;
      return;
    }
    const orderPath = key.slice("category:".length);
    if (orderPath === categoryPath || orderPath.startsWith(`${categoryPath}:`)) return;
    nextOrder[key] = ids;
  });

  return {
    categories: nextCategories,
    folders: nextFolders,
    order: nextOrder,
    childOrder: pruneCategoryChildOrder(childOrderMap, categoryPath),
  };
}

/**
 * Sibling folder names at the same tree level (for rename validation).
 *
 * @param {Record<string, { children: Record<string, object>, components: object[] }>} categoryTree
 * @param {string} categoryPath
 * @returns {string[]}
 */
export function getSiblingCategoryNames(categoryTree, categoryPath) {
  const segments = parseCategoryPath(categoryPath);
  if (segments.length === 0) return [];
  if (segments.length === 1) {
    return Object.keys(categoryTree || {});
  }
  let node = categoryTree;
  for (let i = 0; i < segments.length - 1; i++) {
    node = node?.[segments[i]]?.children;
    if (!node) return [];
  }
  return Object.keys(node || {});
}

/**
 * Union of explicit empty folders + top-level segments inferred from assignments.
 * Preserves explicit folder order, then appends inferred names alphabetically.
 *
 * @param {Record<string, string> | null | undefined} categoriesMap
 * @param {string[] | null | undefined} explicitFolders
 * @returns {string[]}
 */
export function resolveTopLevelCategoryFolderOrder(categoriesMap, explicitFolders) {
  const explicit = (explicitFolders || [])
    .map(normalizeCategoryName)
    .filter(Boolean);
  const explicitSet = new Set(explicit);
  const inferred = new Set();

  if (categoriesMap) {
    Object.values(categoriesMap).forEach((path) => {
      const top = parseCategoryPath(path)[0];
      if (top && !explicitSet.has(top)) inferred.add(top);
    });
  }

  return [...explicit, ...[...inferred].sort((a, b) => a.localeCompare(b))];
}

/**
 * @typedef {{ children: Record<string, CategoryTreeNode>, components: object[] }} CategoryTreeNode
 */

/**
 * Build category tree + uncategorized roots from dashboard meta.
 * Supports migration: versions with only componentCategories (no folder list) still work.
 *
 * @param {object[]} rootComponents — already sorted root component list
 * @param {Record<string, string> | null | undefined} categoriesMap
 * @param {string[] | null | undefined} explicitFolders
 * @returns {{ categoryTree: Record<string, CategoryTreeNode>, uncategorizedRootComponents: object[], topLevelFolderOrder: string[] }}
 */
export function buildCategoryTree(rootComponents, categoriesMap, explicitFolders) {
  const tree = {};
  const uncategorized = [];
  const topLevelFolderOrder = resolveTopLevelCategoryFolderOrder(categoriesMap, explicitFolders);

  topLevelFolderOrder.forEach((name) => {
    if (!tree[name]) tree[name] = { children: {}, components: [] };
  });

  (rootComponents || []).forEach((comp) => {
    const catStr = categoriesMap?.[comp.componentId];
    if (!catStr) {
      uncategorized.push(comp);
      return;
    }
    const segments = parseCategoryPath(catStr);
    if (segments.length === 0) {
      uncategorized.push(comp);
      return;
    }
    let node = tree;
    segments.forEach((seg, idx) => {
      if (!node[seg]) node[seg] = { children: {}, components: [] };
      if (idx === segments.length - 1) {
        node[seg].components.push(comp);
      } else {
        node = node[seg].children;
      }
    });
  });

  return { categoryTree: tree, uncategorizedRootComponents: uncategorized, topLevelFolderOrder };
}
